import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { toast } from 'sonner';
import type { CognitoUser } from '@metron/shared';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('ResetPasswordModal');

interface ResetPasswordModalProps {
  open: boolean;
  user: CognitoUser | null;
  onOpenChange: (open: boolean) => void;
  onResetPassword: (userId: string) => Promise<void>;
}

export function ResetPasswordModal({
  open,
  user,
  onOpenChange,
  onResetPassword,
}: ResetPasswordModalProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleReset = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      await onResetPassword(user.username);
      toast.success('Password reset email sent to user');
      onOpenChange(false);
    } catch (error) {
      const errorMessage = toErrorMessage(error, 'Failed to reset password');
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            Send a password reset email to {user.name} ({user.email})?
          </DialogDescription>
        </DialogHeader>
        <div className='py-4'>
          <p className='text-sm text-muted-foreground'>
            The user will receive an email with instructions to reset their password. They will be
            required to create a new password before they can log in.
          </p>
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleReset} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
